import { push } from 'connected-react-router';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { storePreselectedSubscriptionAction } from "store/app/actions";
import * as actions from './actions';
import * as actionTypes from './constants';
import {
  selectProduct,
  selectProducts,
} from './selectors';

import axios from 'axios';
import currency from 'currency.js';
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { saveAs } from 'file-saver';
import isEqual from 'lodash.isequal';
import moment from 'moment';
import { toast } from "react-toastify";

import { STRIPE_TEST_MODE, VAT_RATE } from "../../config/settings";

import ApiService from '../../services/apiService';
import { URLToFormData } from '../../utils/helpers';

dayjs.extend(utc)
dayjs.extend(customParseFormat)

const scrollToTop = () => {
  document.body.scrollTop = document.documentElement.scrollTop = 0;
}

export function* configRequest() {
  try {
    const [config, planConfig] = yield all([
      call([ApiService, 'getConfig']),
      call([ApiService, 'getPlanConfig']),
    ]);
    yield put(actions.configRequestSuccessAction({ config, planConfig }));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(actions.configRequestCompleteAction());
  }
}

export function* updateConfig(action) {
  scrollToTop();
  try {
    const config = yield call([ApiService, 'updateConfig'], action.payload);
    yield put(actions.updateConfigSuccessAction(config));
    toast.success("Saved!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.updateConfigCompleteAction());
  }
}

export function* getWordPressCategoryData() {
  try {
    const data = yield call([ApiService, "getWordPressCategoryData"]);
    yield put(actions.getWordPressCategoryDataSuccessAction(data));
  } catch (err) {
    toast.error("Error");
    console.error(err);
  } finally {
    yield put(actions.getWordPressCategoryDataCompleteAction());
  }
}

export function* productsRequest() {
  try {
    const result = yield call([ApiService, 'getUserItems'], 'product');
    yield put(actions.productsRequestSuccessAction(result));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(actions.productsRequestCompleteAction());
  }
}

export function* getProductSaga(action) {
  const { slug } = action?.subscription || action;

  try {
    let product;
    if (slug === 'add-listing') {
      product = { slug: 'add-listing', name: '+ Add Listing', plan: 'free' };
    } else {
      product = yield call([ApiService, 'getUserProduct'], slug);
    }

    yield put(actions.getProductSuccess(product));
  } catch (error) {
    toast.error("Could not get product details.")
    console.log(error);
  } finally {
    yield put(actions.getProductComplete());
  }
}

export function* exportReviews() {
  try {
    const product = yield select(selectProduct);
    const reviewsExport = yield call([ApiService, 'reviewsExport'], { product: product.slug });
    const blob = new Blob([reviewsExport], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "reviews.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* exportAllReviews() {
  try {
    const reviewsExport = yield call([ApiService, 'allReviewsExport']);
    const blob = new Blob([reviewsExport], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "all-reviews.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* exportAllProducts(action) {
  try {
    yield call([ApiService, 'allProductsExport'], action.payload);
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* exportAllLeads(action) {
  try {
    const reviewsExport = yield call([ApiService, 'allLeadsExport'], action.payload);
    const blob = new Blob([reviewsExport], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "all-leads.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* exportAllAnonUsers(action) {
  try {
    const anonUsersExport = yield call([ApiService, 'exportAllAnonUsers'], action.payload);
    const blob = new Blob([anonUsersExport], { type: "text/plain;charset=utf-8" });
    saveAs(blob, "all-anon-users.csv");
    toast.success("Export successful!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.exportComplete());
  }
}

export function* createProduct(action) {
  scrollToTop();
  try {
    const { screenshots, logo } = action;
    let product = yield call([ApiService, 'createProduct'], action.product);

    if (!product || product.data?.error) {
      toast.error(product.data.error.description || "Error. Not created.");

    } else {

      if (screenshots) {
        const form = new FormData();
        let i = 0;

        for (const image of screenshots) {
          i++;
          const file = yield call(URLToFormData, [image.source]);
          form.append(`file${i}`, file);
        }
        product.images = yield call([ApiService, 'postMedia'], { form, slug: product.slug, type: 'image', target: 'screenshots' });
      }

      if (logo && logo.source) {
        const form = new FormData();
        const file = yield call(URLToFormData, [logo.source]);
        form.append('file', file);
        product.logo = yield call([ApiService, 'postMedia'], { form, slug: product.slug, type: 'image', target: 'logo' });
      }

      if (product.images || product.logo) {
        product = yield call([ApiService, 'updateProduct'], { product });
      }

      yield put(actions.createProductSuccessAction(product));
      yield put(push(`/products/${product.slug}/profile`));
      toast.success("Listing created!");
    }

  } catch (err) {
    console.log(err);
    toast.error(err.description || "Error. Not created.");
  } finally {
    yield put(actions.createProductCompleteAction());
  }
}

export function* deleteLead(action) {
  try {
    yield call([ApiService, 'deleteLead'], { TYPE: action.lead.TYPE });
    yield put(actions.deleteLeadSuccessAction(action.lead));
    toast.success("Deleted");
  } catch (err) {
    toast.error(err?.description || "Error");
  } finally {
    yield put(actions.deleteLeadCompleteAction());
  }
}

/**
 * Single source of truth for the Evidence form's Save and Publish flow.
 *
 * Handles both edits to existing case studies and uploads of new ones in one
 * pass, ending in a single PUT containing the merged caseStudies array. This
 * replaces the older split between updateEvidence and uploadEvidence sagas,
 * which raced when both were dispatched from the same Save (each read product
 * state independently and last PUT won).
 *
 * Order: existing edits applied first, then new items appended. Per-item
 * updatedAt is bumped only when that item's persisted shape would change.
 */
export function* saveEvidence(action) {
  const { rowsToUpdate = [], rowsToUpload = [] } = action.payload || {};

  try {
    // Yield once so React can render `updatePending: true` before any sync
    // bail-out path dispatches `completeUpdateProduct` in the finally block.
    // Without this, React batches both reducer updates and never sees the
    // transition, leaving the Save button stuck in its loading state.
    yield delay(0);

    if (!rowsToUpdate.length && !rowsToUpload.length) return;

    const NOW = dayjs().utc()
    const products = yield select(selectProducts);
    const product = yield select(selectProduct);
    const { slug } = product;
    const existingCaseStudies = product.caseStudies || [];

    // 1. Apply edits to existing case studies (with a per-item dirty check
    //    so untouched items keep their old updatedAt).
    let anyExistingChanged = false;
    const baseCaseStudies = existingCaseStudies.map(caseStudy => {
      const matchedRow = rowsToUpdate.find(row => isEqual(row.original, caseStudy))
      if (!matchedRow) return caseStudy;

      // Only override `date` if the user actually changed the year. Compare
      // the year string from the form against the year extracted from the
      // existing DB value (which may be "YYYY" post-migration, or legacy
      // "MM/YYYY"/"DD/MM/YYYY" pre-migration). If unchanged, the spread
      // carries the original value through untouched.
      const DATE_FORMATS = ['YYYY', 'MM/YYYY', 'DD/MM/YYYY'];
      const parsedOriginal = dayjs(caseStudy.date, DATE_FORMATS, true);
      const originalYear = parsedOriginal.isValid() ? String(parsedOriginal.year()) : null;
      const yearChanged = !!matchedRow.date && matchedRow.date !== originalYear;

      const candidate = {
        ...caseStudy,
        schoolName: matchedRow.title,
        ...(yearChanged ? { date: matchedRow.date } : {}),
        // Legacy pre-2026 evidence had a separate `location` field. The current form
        // merges schoolName + location into the single Title input on load, so we
        // clear location here to avoid the value double-living in both fields.
        location: '',
        type: matchedRow.type,
        type_custom: matchedRow.type_custom || null,
        // produce_start_date intentionally NOT overridden — the spread preserves
        // any pre-migration value; post-migration records don't carry it (the
        // value lives under `deprecated_produce_start_date`, not touched here).
        producer: matchedRow.producer || null,
        evidence_tags: matchedRow.evidence_tags || [],
        description: matchedRow.description || null,
      };

      if (isEqual(candidate, caseStudy)) return caseStudy;

      anyExistingChanged = true;
      return { ...candidate, updatedAt: NOW.toISOString() };
    })

    // 2. For new items: request signed URLs, push each file to S3, then build
    //    the new case study objects. If S3 upload throws, we bail before the
    //    PUT — existing data is preserved.
    let newCaseStudies = [];
    if (rowsToUpload.length) {
      const files = rowsToUpload.map(study => study.file.name);
      const signedUrls = yield call([ApiService, 'postEvidence'], { files, slug });

      for (let i = 0; i < rowsToUpload.length; i++) {
        const study = rowsToUpload[i];
        const signedUrl = signedUrls[i];
        const formData = new FormData();
        Object.entries(signedUrl.fields).forEach(([k, v]) => {
          formData.append(k, v);
        });
        formData.append("file", study.file);
        yield call(axios.post, signedUrl.url, formData);
      }

      newCaseStudies = rowsToUpload.map((study, index) => {
        const keyParts = signedUrls[index].fields.key.split('/');
        return {
          link: {
            type: 'file',
            location: keyParts[keyParts.length - 1],
          },
          schoolName: study.title,
          // Year-only string straight from the form's year picker.
          date: study.date || null,
          // Legacy pre-2026 evidence had a separate `location` field. The current form
          // has no location input — Title is the only name-of-thing field — so we write
          // an empty string for shape consistency with older records.
          location: '',
          type: study.type,
          type_custom: study.type_custom || null,
          // produce_start_date intentionally omitted — retired in Phase 2.
          producer: study.producer || null,
          evidence_tags: study.evidence_tags || [],
          description: study.description || null,
          createdAt: NOW.toISOString(),
          updatedAt: NOW.toISOString(),
        };
      });
    }

    // 3. Nothing actually changed? Skip the PUT — the product-level updatedAt
    //    (auto-stamped by dynogels on UpdateItem) shouldn't bump either.
    if (!anyExistingChanged && !newCaseStudies.length) {
      toast.info("Nothing to save.");
      return;
    }

    // 4. One PUT carrying both edits and new items.
    const newProduct = {
      ID: product.ID,
      TYPE: product.TYPE,
      slug: product.slug,
      caseStudies: [...baseCaseStudies, ...newCaseStudies],
    }

    const result = yield call([ApiService, 'updateProduct'], { product: newProduct });

    const updatedProducts = products.map(p => {
      if (p.slug === result.slug) return result;
      return p;
    });
    yield put(actions.productsRequestSuccessAction(updatedProducts));
    yield put(actions.getProductSuccess(result));

    toast.success("Saved!");

  } catch (error) {
    let errorMessage = "Error. Not saved."
    if (error.response && error.response.data.status >= 400 && error.response.data.status <= 599) {
      errorMessage = error.response.data.error.description
    }
    toast.error(errorMessage);
    console.error({ error });
  } finally {
    yield put(actions.completeUpdateProduct());
  }
}

export function* deleteEvidence(action) {
  try {
    const products = yield select(selectProducts);
    const { slug } = yield select(selectProduct);
    const result = yield call([ApiService, 'deleteEvidence'], { slug, id: action.payload });
    const updatedProducts = products.map(p => {
      if (p.slug === result.slug) return result;
      return p;
    });
    yield put(actions.productsRequestSuccessAction(updatedProducts));
    yield put(actions.getProductSuccess(result));
    toast.success("Deleted!");

  } catch (err) {
    console.log(err);
    toast.error("Error. Not deleted.");
  } finally {
    yield put(actions.completeUpdateProduct());
  }
}

export function* updateProduct(action) {
  scrollToTop();

  const { screenshots, logo } = action;

  try {
    const product = yield select(selectProduct);
    if (!product) return;

    const { slug } = product;
    const newProduct = {
      ...action.product,
      slug,
    };

    if (screenshots) {
      const form = new FormData();
      let i = 0;

      for (const image of screenshots) {
        i++;
        const file = yield call(URLToFormData, [image.source]);
        form.append(`file${i}`, file);
      }
      const res = yield call([ApiService, 'postMedia'], { form, slug, type: 'image', target: 'screenshots' });
      newProduct.images = res
      // Hacky, but mark all uploads as done, remove name and size details so RUG doesn't show it.
      // ? What if the postScreenshots/postMedia call fails?
      newProduct.screenshots.forEach((s, index) => {
        s.done = true
        // No longer a blob! Switch to URL.
        s.source = res[index]
        s.name = null
        s.size = null
      })
    }

    if (logo && logo.source) {
      const form = new FormData();
      const file = yield call(URLToFormData, [logo.source]);
      form.append('file', file);
      newProduct.logo = yield call([ApiService, 'postMedia'], { form, slug, type: 'image', target: 'logo' });
    }

    const result = yield call([ApiService, 'updateProduct'], { product: newProduct });

    // Should the call fail, don't attempt to update the product in our store and cause the app to behave awkward (example: loses published status, loses premium status)
    if (result.status >= 400 && result.status <= 599) {
      throw result.data.error
    }

    if (action.product.newSlug) {
      yield delay(3000);
      yield call(productsRequest);
      yield put(push(`/products/${action.product.newSlug}/admin`));

    } else {
      yield put(actions.getProductSuccess(result));
    }

    toast.success("Saved!");

  } catch (err) {
    let errorMessage = "Error. Not saved."
    if (err?.retryable) {
      errorMessage += ` Please try again after ${moment.duration(err?.retryDelay || 180).humanize()}.`
    }

    toast.error(errorMessage);
    console.log(err);
  } finally {
    yield put(actions.completeUpdateProduct());
  }
}

export function* reviewsRequest(action) {
  scrollToTop();

  const product = action.payload.slug;
  const page = action.payload.page;
  const featured = action.payload.featured;
  const reviewPreference = action.payload.reviewPreference;
  const includeEim = action.payload.includeEim || false;
  const perPage = action.payload.perPage || 10;

  try {
    const result = yield call([ApiService, 'reviews'], {
      product,
      page,
      featured,
      reviewPreference,
      perPage,
      includeEim,
    });
    if (includeEim) {
      yield put(actions.eimReviewsRequestSuccessAction(result));
    } else {
      yield put(actions.reviewsRequestSuccessAction(result));
    }
  } catch (err) {
    console.log(err);
  } finally {
    yield put(actions.reviewsRequestCompleteAction());
  }
}

export function* leadsRequest(action) {
  scrollToTop();

  const slug = action.payload.slug;

  try {
    const result = yield call([ApiService, 'leads'], {
      slug
    });
    yield put(actions.leadsRequestSuccessAction(result));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(actions.leadsRequestCompleteAction());
  }
}

export function* invitesRequest(action) {
  scrollToTop();
  const { page = 1, perPage = 50, slug } = action.payload;

  try {
    const result = yield call([ApiService, 'getInvites'], {
      slug,
      page,
      perPage,
    });
    yield put(actions.invitesRequestSuccessAction(result));
  } catch (err) {
    console.log(err);
  } finally {
    yield put(actions.invitesRequestCompleteAction());
  }
}

export function* invitesUpload(action) {
  scrollToTop();
  try {
    const product = yield select(selectProduct);
    const result = yield call([ApiService, 'createInvites'], {
      slug: product.slug,
      invites: action.payload.invites,
      sendDate: action.payload.sendDate,
      isTest: action.payload.isTest || false,
    });
    yield put(actions.invitesUploadSuccessAction(result));
    toast.success("Success!");

    if (result.length > 0) {
      yield put(push(`/products/${product.slug}/reviews/invitations`));
    } else {
      const productInvites = product.invites || {};
      yield put(actions.getProductSuccess({
        ...product,
        invites: {
          ...productInvites,
          sentTest: true,
        },
      }));
    }

  } catch (err) {
    const errorData = err?.response?.data?.error?.description;
    if (errorData?.code === 'FREE_PLAN_LIMIT_EXCEEDED') {
      const { remaining } = errorData.details;
      toast.error(remaining > 0
        ? `You can only upload up to ${remaining} more invite${remaining === 1 ? '' : 's'} this year on the free plan.`
        : `You've reached the free plan limit of ${errorData.details.maxAllowed} invites per year.`
      );
    } else {
      toast.error("Error.");
    }
    console.log(err);
  } finally {
    yield put(actions.invitesUploadCompleteAction());
  }
}

export function* reviewReply(action) {
  try {
    const result = yield call([ApiService, 'replyToReview'], action.payload);
    yield put(actions.reviewReplySuccessAction({
      ...action.payload.review,
      reply: action.payload.reply,
    }));
    toast.success("Success!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.reviewReplyCompleteAction());
  }
}

export function* reviewDelete(action) {
  try {
    const result = yield call([ApiService, 'deleteReview'], action.payload.id);
    yield put(actions.reviewDeleteSuccessAction(result));
    toast.success("Success!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.reviewDeleteCompleteAction());
  }
}

export function* leadReply(action) {
  try {
    const result = yield call([ApiService, 'replyToLead'], action.payload);
    yield put(actions.leadReplySuccessAction(result));
    toast.success("Success!");
  } catch (err) {
    toast.error("Error.");
    console.log(err);
  } finally {
    yield put(actions.leadReplyCompleteAction());
  }
}

export function* setLeadStatus(action) {
  try {
    const lead = yield call([ApiService, 'setLeadStatus'], action.lead.TYPE, action.status);
    yield put(actions.setLeadStatusSuccessAction(lead));
    toast.success("Moved");
  } catch (err) {
    toast.error(err?.description || "Error");
  } finally {
    yield put(actions.setLeadStatusCompleteAction());
  }
}

export function* createPaymentIntent(action) {
  if (action.scrollToTop) scrollToTop();

  try {
    if (!action.amount) {
      yield put(actions.createPaymentIntentSuccessAction());
      yield put(actions.createPaymentIntentCompleteAction());

    } else {
      const { slug } = yield select(selectProduct);
      const amount = Math.round((action.amount * 100 + Number.EPSILON) * 100) / 100; // Convert £ to p
      const paymentIntent = yield call([ApiService, 'createPaymentIntent'], { slug, amount, testMode: STRIPE_TEST_MODE || false });

      if (paymentIntent.client_secret) {
        yield put(actions.createPaymentIntentSuccessAction(paymentIntent));
      } else {
        toast.error(paymentIntent.data?.error?.description || 'Error');
      }
    }

  } catch (err) {
    console.log(err);
    toast.error(err.description || "Error");

  } finally {
    yield put(actions.createPaymentIntentCompleteAction());
  }
}

export function* confirmPayment(action) {
  try {
    const { stripe, card, creditAmount, leadsBought = null, clientSecret, supabaseUserId } = action.payload;
    // Cast string to number.
    const amount = Number(creditAmount);

    const { slug, leadCreditBalance, pricePerLead, paidLeadCount } = yield select(selectProduct);
    const isLeadBundleUser = !!pricePerLead && pricePerLead > 0
    const result = yield call([stripe, 'confirmCardPayment'], clientSecret, {
      payment_method: { card }
    });

    if (result.paymentIntent?.status === 'succeeded') {
      const transaction = yield call([ApiService, 'newLeadCreditsTransaction'], {
        slug,
        amount: amount,
        tax: currency(amount, { precision: 2 }).multiply(VAT_RATE).value,
        leadCredits: leadsBought,
        stripeTransactionId: result.paymentIntent.id,
        note: 'Top-up',
        supabaseUserId
      })
      toast.success("Success!");
      // This just updates the lead credit balance for the product, really.
      // We don't need to update history locally, here, since the history is always fetched fresh when you open that tab.
      yield put(actions.confirmPaymentSuccessAction({
        newMonetaryBalance: !isLeadBundleUser ? leadCreditBalance + amount : leadCreditBalance,
        newPaidLeadCount: isLeadBundleUser ? paidLeadCount + leadsBought : paidLeadCount,
        paymentIntent: result.paymentIntent
      }));

    } else {
      console.log(result.error.message);
      yield put(actions.confirmPaymentErrorAction(result.error.message));
    }

  } catch (err) {
    console.log(err);
    toast.error("Error");

  } finally {
    yield put(actions.confirmPaymentCompleteAction());
  }
}

export function* createSubscription(action) {
  scrollToTop();

  if (!action.payload) {
    yield put(actions.createSubscriptionSuccessAction());
    yield put(actions.createSubscriptionCompleteAction());
    yield put(storePreselectedSubscriptionAction(null));

  } else {
    try {
      const { slug } = yield select(selectProduct);
      const subscription = yield call([ApiService, 'createSubscription'], { slug, ...action.payload });

      if (subscription.subscriptionId) {
        yield put(actions.createSubscriptionSuccessAction({
          slug,
          ...subscription,
        }));
      } else {
        toast.error(subscription.data?.error?.description || 'Error');
      }

    } catch (err) {
      console.log(err);
      toast.error(err.description || "Error");

    } finally {
      yield put(actions.createSubscriptionCompleteAction());
    }
  }
}

export function* applyCoupon(action) {
  try {
    scrollToTop();
    const { code } = action.payload;
    const coupon = yield call([ApiService, 'getCoupon'], code);

    if (coupon?.valid) {
      toast.success("Success!");
      yield put(actions.applyCouponSuccessAction(coupon));
    } else {
      toast.error("Code not valid");
    }

  } catch (err) {
    console.log(err);
    toast.error(err.message || "Error");

  } finally {
    yield put(actions.applyCouponCompleteAction());
  }
}

export default function* watch() {
  yield takeLatest(actionTypes.PRODUCTS_REQUEST, productsRequest);
  yield takeLatest(actionTypes.CONFIG_REQUEST, configRequest);
  yield takeLatest(actionTypes.CREATE_PRODUCT, createProduct);
  yield takeLatest(actionTypes.UPDATE_PRODUCT, updateProduct);
  yield takeLatest(actionTypes.GET_PRODUCT, getProductSaga);
  yield takeLatest(actionTypes.EXPORT_REVIEWS, exportReviews);
  yield takeLatest(actionTypes.EXPORT_ALL_REVIEWS, exportAllReviews);
  yield takeLatest(actionTypes.EXPORT_ALL_LEADS, exportAllLeads);
  yield takeLatest(actionTypes.EXPORT_ALL_ANON_USERS, exportAllAnonUsers);
  yield takeLatest(actionTypes.EXPORT_ALL_PRODUCTS, exportAllProducts);
  yield takeLatest(actionTypes.REVIEWS_REQUEST, reviewsRequest);
  yield takeLatest(actionTypes.EIM_REVIEWS_REQUEST, reviewsRequest);
  yield takeLatest(actionTypes.LEADS_REQUEST, leadsRequest);
  yield takeLatest(actionTypes.INVITES_REQUEST, invitesRequest);
  yield takeLatest(actionTypes.INVITES_UPLOAD, invitesUpload);
  yield takeLatest(actionTypes.SAVE_EVIDENCE, saveEvidence);
  yield takeLatest(actionTypes.DELETE_EVIDENCE, deleteEvidence);
  yield takeLatest(actionTypes.UPDATE_CONFIG, updateConfig);
  yield takeLatest(actionTypes.WP_CATEGORY_DATA, getWordPressCategoryData);
  yield takeLatest(actionTypes.REVIEW_REPLY, reviewReply);
  yield takeLatest(actionTypes.REVIEW_DELETE, reviewDelete);
  yield takeLatest(actionTypes.LEAD_REPLY, leadReply);
  yield takeLatest(actionTypes.SET_LEAD_STATUS, setLeadStatus);
  yield takeLatest(actionTypes.DELETE_LEAD, deleteLead);
  yield takeLatest(actionTypes.PAYMENT_INTENT, createPaymentIntent);
  yield takeLatest(actionTypes.CONFIRM_PAYMENT, confirmPayment);
  yield takeLatest(actionTypes.CREATE_SUBSCRIPTION, createSubscription);
  yield takeLatest(actionTypes.APPLY_COUPON, applyCoupon);
  yield takeLatest(actionTypes.CREATE_SUBSCRIPTION_SUCCESS, getProductSaga);
}
